import { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import { Tag } from "components/designV2/tag/tag";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export type SearchFieldState = "inactive" | "disabled" | "active" | "filled";

/** Props for the search field component. */
export interface SearchFieldProps {
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  leadingContent?: React.ReactNode;
  allowTags?: boolean;
  tags?: string[];
  maxVisibleTags?: number;
  onTagsChange?: (tags: string[]) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

/** Search input with optional tag support and clear affordances. */
export const SearchField = ({
  value,
  placeholder,
  disabled,
  leadingContent,
  allowTags,
  tags,
  maxVisibleTags = 3,
  onTagsChange,
  onKeyDown,
  onChange,
  onClear,
  onFocus,
  onBlur,
}: SearchFieldProps): JSX.Element => {
  const [isFocused, setIsFocused] = useState(false);
  const isFilled = Boolean(value && value.length > 0);

  const visualState = useMemo<SearchFieldState>(() => {
    if (disabled) return "disabled";
    if (isFocused) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [disabled, isFocused, isFilled]);

  const handleFocus = (): void => {
    if (disabled) return;
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    onKeyDown?.(event);
    if (!allowTags || disabled || event.defaultPrevented) return;
    if (event.key === "Enter" && value?.trim()) {
      const next = value.trim().slice(0, 20);
      const currentTags = tags ?? [];
      if (!currentTags.includes(next)) {
        onTagsChange?.([...currentTags, next]);
        onChange?.("");
      }
      event.preventDefault();
    }
  };

  const handleRemoveTag = (tag: string): void => {
    if (!tags) return;
    onTagsChange?.(tags.filter((item) => item !== tag));
  };

  const handleClear = (): void => onClear?.();
  const resolvedPlaceholder =
    allowTags && tags && tags.length > 0 ? "" : placeholder;
  const resolvedMaxLength = allowTags ? 20 : undefined;
  const visibleTags = tags?.slice(0, maxVisibleTags) ?? [];
  const hiddenTagCount = Math.max((tags?.length ?? 0) - visibleTags.length, 0);
  const hiddenTagsLabel = tags?.slice(visibleTags.length).join(", ") ?? "";

  return (
    <Container aria-disabled={disabled}>
      <FieldWrapper $state={visualState}>
        <LeadingIcon>
          <CustomIcon
            name="v2/search"
            size="24px"
            color={
              visualState === "active" || visualState === "filled"
                ? ColorV2.Blue1
                : ColorV2.Blue5
            }
          />
        </LeadingIcon>
        {leadingContent && <LeadingContent>{leadingContent}</LeadingContent>}
        {allowTags && tags && tags.length > 0 && (
          <TagList>
            {visibleTags.map((tag) => (
              <Tag
                key={tag}
                text={tag}
                onClear={disabled ? undefined : () => handleRemoveTag(tag)}
              />
            ))}
            {hiddenTagCount > 0 && (
              <TooltipHover text={hiddenTagsLabel} iconName="v2/info">
                <MoreTags>{`+${hiddenTagCount}`}</MoreTags>
              </TooltipHover>
            )}
          </TagList>
        )}
        <Input
          type="text"
          value={value}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          $state={visualState}
          maxLength={resolvedMaxLength}
          onChange={(event) => onChange?.(event.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        {isFilled && !disabled && (
          <TrailingIcon role="button" onClick={handleClear}>
            <CustomIcon name="v2/cross" size="24px" color={ColorV2.Blue5} />
          </TrailingIcon>
        )}
      </FieldWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: ${V2_INPUT_WIDTH_PX}px;
  display: flex;
  flex-direction: column;
`;

const FieldWrapper = styled.div<{
  $state: SearchFieldState;
}>`
  display: flex;
  align-items: center;
  height: ${V2_INPUT_HEIGHT_PX}px;
  padding: 0 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
  overflow: visible;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
    `}
`;

const Input = styled.input<{ $state: SearchFieldState }>`
  ${TypographyV2.b2}
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  color: ${ColorV2.Blue1};

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$state === "inactive" &&
    css`
      color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$state === "active" &&
    css`
      color: ${ColorV2.Blue1};

      &::placeholder {
        color: ${ColorV2.Blue1};
      }
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      color: ${ColorV2.Blue5};
      cursor: not-allowed;
    `}

  ${(props) =>
    props.$state === "filled" &&
    css`
      color: ${ColorV2.Blue1};
    `}
`;

const LeadingIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const LeadingContent = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
`;

const TagList = styled.div`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  overflow: visible;
  max-width: 100%;
  flex: 0 1 auto;
`;

const MoreTags = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  background: ${ColorV2.Blue6};
  border-radius: 8px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  white-space: nowrap;
`;

const TrailingIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  cursor: pointer;
`;
